<script>
   /*************************************************************/
   /* AppContainer for training                                 */
   /* -------------------------                                 */
   /* app container for training of PCA model                   */
   /*************************************************************/

   import { untrack } from 'svelte';

   import { replacer } from '../../../shared/utils.js';
   import DataLoader from '../../../shared/components/PCADataLoader.svelte';
   import ResultTable from '../shared/ModelTable.svelte';
   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import Contributions from '../../../shared/components/Contributions.svelte';
   import ModelCalculator from '../../../shared/components/PCAModelCalculator.svelte';

   import { downloadResults, downloadModel } from '../utils';

   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['train']);

   // properties for showing contributions
   let selected = $state(-1);
   let ncomp = $state(1);
   let contributionIsShown = $state(false);


   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {
      // check that position is valid
      if (pos < 0 || pos > s.resobj.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.resobj.objindices[pos];

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.model = null;
      }
   }

   // show modal dialog with contributions for selected object
   function showContributions(pos, ncomp) {
      if (pos < 0) return;
      selected = pos;
      ncomp = ncomp;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
      ncomp = 1;
   }

   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.model = null;
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.model = null;
   }


   // effect to track changes in dataset and models
   $effect(() => {
      if (s.dataset === null) {
         // if training set is set null - reset model and all related parameters
         s.model = null;
         s.outliers = [];
      }

      // if model object is set to null - reset dependent objects
      if (s.model === null) {
         s.resobj = null;
      }
   });

   $effect(() => {
      const sessionStr = untrack(() => JSON.stringify(session, replacer));
      backupSession(sessionStr, s.model, s.dataset, s.resobj);
   })

</script>

{#if contributionIsShown}
<Contributions
   {...s}
   {ncomp}
   selected={s.resobj.objindices[selected]}
   closeModal={hideContributions}
/>
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={s.dataset} className="Group" />
      </div>

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.dataset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      {#if s.dataset }
      <div class="model-calculator">
         <ModelCalculator
            {WASM}
            {downloadModel}
            {downloadResults}
            dataset={s.dataset}
            outliers={s.outliers}
            bind:model={s.model}
            bind:resobj={s.resobj}
         />
      </div>
      {/if}

      {#if s.resobj}
      <div class="result-stats">
         <ResultTable resobj={s.resobj} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.resobj || !s.model}
         <div class="help-text">
         {#if !s.dataset}
            <p>
               In this tab you can train a PCA model and explore how well it describes the training set. After creating the model you can save it to a file and apply it to another, new set of samples, and see how well this new set is explained by the model (tab <strong>Project</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables.
            </p>
            <p>
               First row in the CSV file can contain variable labels or values (<strong>header</strong>). It is assumed that variable lables are text (or have text parts), e.g. <em>"Na"</em>, <em>"OD260"</em>, etc. If variables are part of signals, e.g. spectra, the header can content numeric values, e.g. wavelength or wavenumbers. If this is the case, select <em>values</em> option for the header. In this case you will also be able to specify name of the values (e.g. <em>Wavelength</em>) and units (e.g. <em>nm</em>) if necessary.
            </p>
            <p>
               First column in the file can contain labels for the objects/observations (<strong>row labels</strong>) — just set the corresponding options after selecting the file. It is also possible to have group labels, which you can use, for example, to color group samples on the score plot. Here is an example how data structure may look like (note, that column with group, row labels and header are optional):
            </p>
            <div>
               <table>
                  <thead>
                  <tr>
                     <td>Name</td>
                     <td>Group</td>
                     <td>x1</td>
                     <td>x2</td>
                     <td>x3</td>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                     <td>O1</td>
                     <td>A</td>
                     <td>3.8</td>
                     <td>1.3</td>
                     <td>13.6</td>
                  </tr>
                  <tr>
                     <td>O2</td>
                     <td>A</td>
                     <td>11.1</td>
                     <td>12.3</td>
                     <td>3.3</td>
                  </tr>
                  <tr>
                     <td>O3</td>
                     <td>A</td>
                     <td>1.2</td>
                     <td>2.3</td>
                     <td>4.2</td>
                  </tr>
                  </tbody>
               </table>
            </div>
            <p></p>
         {:else}
            {#if !s.model}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PCA model for the training set. Use more components/latent variables (PCs) than you expect to be optimal. You will have possibility to select number of components for every plot.
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key s.resobj }
         <AppPlots {markOutlier} {showContributions} {...s} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table thead tr td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table tbody td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>